import React, {lazy, Suspense, useState} from 'react';

import {reportToSentry} from 'app/utils/sentry';

import type {ComponentType} from 'react';

type OptsType<E, T> = {
  loader: () => Promise<{
    default: ComponentType<T>;
  }>;
  LoadingComponent: ComponentType<
    T & {
      error?: E;
    }
  >;
};

type ErrorState<E> = void | E;

export function Loadable<E, T>({
  loader,
  LoadingComponent,
}: OptsType<E, T>): ComponentType<T> {
  function getLazyComponent(setError: (err: ErrorState<E>) => void) {
    return lazy(() =>
      loader().catch((error) => {
        reportToSentry(error, undefined, false);
        setError(error);
        // fake component, never will be used
        return {
          default: () => null,
        };
      })
    );
  }

  let LazyLoadedComponent: void | ComponentType<T> = undefined;
  return function LazyLoadedWrapperComponent(props: T) {
    const [error, setError] = useState<ErrorState<E>>(undefined);

    if (LazyLoadedComponent == null) {
      LazyLoadedComponent = getLazyComponent(setError);
    }

    return error != null ? (
      <LoadingComponent {...props} error={error} />
    ) : (
      <Suspense fallback={<LoadingComponent {...props} error={undefined} />}>
        <LazyLoadedComponent {...props} />
      </Suspense>
    );
  };
}
